import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import Text from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import clsx from 'clsx';
import { CopyIcon } from 'lucide-react';
import React, { type FC, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'usehooks-ts';

import type { SixDigitCode } from '../../../types/utils/sixDigitCode';

import styles from './styles.module.css';

const Code: FC<{ code: SixDigitCode; id: string }> = ({ code, id }) => {
  const [showButton, setShowButton] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation('general');
  const [, copy] = useCopyToClipboard();

  useEffect(() => {
    return () => {
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    const success = await copy(code);
    if (success) {
      toast.success(`${t('copied')}"${code}"`);
      setShowButton(false); // Hide the button immediately after copy
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
        hoverTimeout.current = null;
      }
    } else {
      toast.error(t('failedCopy'));
    }
  }, [code, copy, t]);

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    hoverTimeout.current = setTimeout(() => setShowButton(true), 200);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setShowButton(false);
  }, []);

  const handleFocus = useCallback(() => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    hoverTimeout.current = setTimeout(() => setShowButton(true), 200);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        handleCopy();
      }
    },
    [handleCopy],
  );

  const handleBlur = useCallback(() => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setShowButton(false);
  }, []);

  return (
    <div
      className={styles.codeWrapper}
      id={id}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
      role="region"
    >
      <Stack
        alignItems="center"
        className={clsx(styles.code, showButton && styles.isOpen)}
        gap={2}
        orientation="horizontal"
      >
        <Text as="h6">{t('code')}</Text>
        <Text as="strong">{code}</Text>
      </Stack>

      <Button
        className={styles.copyButtonOverlay}
        data-testid={`${id}-copy-btn`}
        fullWidth
        onPress={handleCopy}
        size="xs"
        visual="default"
      >
        <CopyIcon size={14} /> {code}
      </Button>
    </div>
  );
};

Code.displayName = 'Code';

export default Code;
